import { normalizeJudgement } from '@/components/review/consensus';
import type { ConsolidatedEventCluster, SessionEvent } from '@/types';
import { getDecisionLabel, getJudgementLabel } from '@/types';

export type DecisionFilterCategory =
  | 'judgement'
  | 'on_field'
  | 'on_field_punishment'
  | 'reviewed'
  | 'reviewed_punishment';

export interface DecisionFilterOption {
  category: DecisionFilterCategory;
  value: string;
  label: string;
  count: number;
  percent: number;
}

export interface ActiveDecisionFilter {
  category: DecisionFilterCategory;
  value: string;
}

export const FILTER_CATEGORY_LABELS: Record<DecisionFilterCategory, string> = {
  judgement: '判決評價',
  on_field: '場上判決',
  on_field_punishment: '場上罰則',
  reviewed: '覆核判決',
  reviewed_punishment: '覆核罰則',
};

function bucketKey(category: DecisionFilterCategory, value: string): string {
  return `${category}:${value}`;
}

function addCount(map: Map<string, DecisionFilterOption>, category: DecisionFilterCategory, value?: string | null) {
  if (!value) return;
  const key = bucketKey(category, value);
  const existing = map.get(key);
  if (existing) {
    existing.count += 1;
  } else {
    const label =
      category === 'judgement'
        ? getJudgementLabel(value === 'incorrect' ? 'wrong' : value)
        : getDecisionLabel(value);
    map.set(key, { category, value, label, count: 1, percent: 0 });
  }
}

export function buildDecisionFilterOptions(events: SessionEvent[]): DecisionFilterOption[] {
  const map = new Map<string, DecisionFilterOption>();
  for (const event of events) {
    if (!event.on_field_decision && !event.judgement) continue;
    addCount(map, 'on_field', event.on_field_decision);
    addCount(map, 'on_field_punishment', event.on_field_punishment);
    if (event.judgement) {
      const j = normalizeJudgement(event.judgement);
      if (j === 'correct') addCount(map, 'judgement', 'correct');
      else if (j === 'incorrect') addCount(map, 'judgement', 'wrong');
      else if (j === 'uncertain') addCount(map, 'judgement', 'uncertain');
    }
    addCount(map, 'reviewed', event.reviewed_decision);
    addCount(map, 'reviewed_punishment', event.reviewed_punishment);
  }
  const options = Array.from(map.values());
  const categoryTotals = new Map<DecisionFilterCategory, number>();
  for (const o of options) {
    categoryTotals.set(o.category, (categoryTotals.get(o.category) || 0) + o.count);
  }
  return options
    .map((o) => ({
      ...o,
      percent: Math.round((o.count / (categoryTotals.get(o.category) || 1)) * 100),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'zh-HK'));
}

export function groupFilterOptions(options: DecisionFilterOption[]) {
  const groups: Record<DecisionFilterCategory, DecisionFilterOption[]> = {
    judgement: [],
    on_field: [],
    on_field_punishment: [],
    reviewed: [],
    reviewed_punishment: [],
  };
  for (const o of options) {
    groups[o.category].push(o);
  }
  return groups;
}

export function filterOptionKey(filter: ActiveDecisionFilter | DecisionFilterOption): string {
  return `${filter.category}:${filter.value}`;
}

export function eventMatchesFilters(
  event: SessionEvent,
  filters: ActiveDecisionFilter[]
): boolean {
  if (filters.length === 0) return true;
  return filters.some((f) => eventMatchesFilter(event, f));
}

export function clusterMatchesFilters(
  cluster: ConsolidatedEventCluster,
  eventMap: Map<string, SessionEvent>,
  filters: ActiveDecisionFilter[]
): boolean {
  if (filters.length === 0) return true;
  return filters.some((f) => clusterMatchesFilter(cluster, eventMap, f));
}

export function eventMatchesFilter(event: SessionEvent, filter: ActiveDecisionFilter | null): boolean {
  if (!filter) return true;
  switch (filter.category) {
    case 'judgement': {
      const j = normalizeJudgement(event.judgement);
      const api =
        j === 'correct' ? 'correct' : j === 'incorrect' ? 'wrong' : j === 'uncertain' ? 'uncertain' : '';
      return api === filter.value;
    }
    case 'on_field':
      return event.on_field_decision === filter.value;
    case 'on_field_punishment':
      return (event.on_field_punishment || 'no_penalty') === filter.value;
    case 'reviewed':
      return event.reviewed_decision === filter.value;
    case 'reviewed_punishment':
      return (event.reviewed_punishment || 'no_penalty') === filter.value;
    default:
      return true;
  }
}

export function clusterMatchesFilter(
  cluster: ConsolidatedEventCluster,
  eventMap: Map<string, SessionEvent>,
  filter: ActiveDecisionFilter | null
): boolean {
  if (!filter) return true;
  const anchor = cluster.anchorEventId ? eventMap.get(cluster.anchorEventId) : undefined;
  const pseudo: SessionEvent = anchor || {
    id: cluster.clusterId,
    session_id: '',
    session_type: 'review',
    event_type: 'decision',
    marker_role: 'official',
    on_field_decision: cluster.onFieldBall,
    on_field_punishment: cluster.dominantSanction,
    created_by_user_id: 0,
    created_at: '',
    updated_at: '',
  };
  return eventMatchesFilter(pseudo, filter);
}
